import { z } from "zod"

export enum PayoutTransferStatus {
  PAID = "PAID",
  PENDING = "PENDING",
  IN_TRANSIT = "IN_TRANSIT",
  CANCELED = "CANCELED",
  FAILED = "FAILED",
}

export enum TransferMethod {
  INSTANT = "INSTANT",
  STANDARD = "STANDARD",
}

export enum BalanceTransactionType {
  ADJUSTMENT = "ADJUSTMENT",
  ADVANCE = "ADVANCE",
  ADVANCE_FUNDING = "ADVANCE_FUNDING",
  ANTICIPATION_REPAYMENT = "ANTICIPATION_REPAYMENT",
  APPLICATION_FEE = "APPLICATION_FEE",
  APPLICATION_FEE_REFUND = "APPLICATION_FEE_REFUND",
  CHARGE = "CHARGE",
  CLIMATE_ORDER_PURCHASE = "CLIMATE_ORDER_PURCHASE",
  CLIMATE_ORDER_REFUND = "CLIMATE_ORDER_REFUND",
  CONNECT_COLLECTION_TRANSFER = "CONNECT_COLLECTION_TRANSFER",
  CONTRIBUTION = "CONTRIBUTION",
  ISSUING_AUTHORIZATION_HOLD = "ISSUING_AUTHORIZATION_HOLD",
  ISSUING_AUTHORIZATION_RELEASE = "ISSUING_AUTHORIZATION_RELEASE",
  ISSUING_DISPUTE = "ISSUING_DISPUTE",
  ISSUING_TRANSACTION = "ISSUING_TRANSACTION",
  OBLIGATION_OUTBOUND = "OBLIGATION_OUTBOUND",
  OBLIGATION_REVERSAL_INBOUND = "OBLIGATION_REVERSAL_INBOUND",
  PAYMENT = "PAYMENT",
  PAYMENT_FAILURE_REFUND = "PAYMENT_FAILURE_REFUND",
  PAYMENT_NETWORK_RESERVE_HOLD = "PAYMENT_NETWORK_RESERVE_HOLD",
  PAYMENT_NETWORK_RESERVE_RELEASE = "PAYMENT_NETWORK_RESERVE_RELEASE",
  PAYMENT_REFUND = "PAYMENT_REFUND",
  PAYMENT_REVERSAL = "PAYMENT_REVERSAL",
  PAYMENT_UNRECONCILED = "PAYMENT_UNRECONCILED",
  PAYOUT = "PAYOUT",
  PAYOUT_CANCEL = "PAYOUT_CANCEL",
  PAYOUT_FAILURE = "PAYOUT_FAILURE",
  REFUND = "REFUND",
  REFUND_FAILURE = "REFUND_FAILURE",
  RESERVE_TRANSACTION = "RESERVE_TRANSACTION",
  RESERVED_FUNDS = "RESERVED_FUNDS",
  STRIPE_FEE = "STRIPE_FEE",
  STRIPE_FX_FEE = "STRIPE_FX_FEE",
  TAX_FEE = "TAX_FEE",
  TOPUP = "TOPUP",
  TOPUP_REVERSAL = "TOPUP_REVERSAL",
  TRANSFER = "TRANSFER",
  TRANSFER_CANCEL = "TRANSFER_CANCEL",
  TRANSFER_FAILURE = "TRANSFER_FAILURE",
  TRANSFER_REFUND = "TRANSFER_REFUND",
}

export const PayoutItemDtoSchema = z.object({
  id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.nativeEnum(PayoutTransferStatus),
  last4Digits: z.string().nullable(),
  bankName: z.string().nullable(),
  createdAt: z.string().datetime(),
  expectedReceptionDate: z.string().datetime(),
})

export const PayoutTransactionDtoSchema = z.object({
  id: z.string(),
  transactionId: z.string().nullable(),
  type: z.nativeEnum(BalanceTransactionType),
  amount: z.number(),
  currency: z.string(),
  fee: z.number(),
  name: z.string().nullish(),
  createdAt: z.string().datetime(),
})

export const TransactionSummaryDtoSchema = z.object({
  number: z.number(),
  feeAmount: z.number().nullish(),
  totalAmount: z.number().nullish(),
  currency: z.string().nullish(),
})

export const PayoutDetailsDtoSchema = z.object({
  id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.nativeEnum(PayoutTransferStatus),
  last4Digits: z.string().nullable(),
  bankName: z.string().nullable(),
  createdAt: z.string().datetime(),
  expectedReceptionDate: z.string().datetime(),
  intervalStartDate: z.string().nullish(),
  intervalEndDate: z.string().nullish(),
  bankCountry: z.string(),
  routingNumber: z.string().nullish(),
  transferTrackingId: z.string().nullish(),
  note: z.string().nullish(),
  transferMethod: z.nativeEnum(TransferMethod),
  transactions: z.array(PayoutTransactionDtoSchema), // List of transactions (charges, refunds & adjustments) for this payout
  paymentSummary: TransactionSummaryDtoSchema,
  refundSummary: TransactionSummaryDtoSchema,
  adjustmentSummary: TransactionSummaryDtoSchema,
})

export type PayoutTransactionDto = z.infer<typeof PayoutTransactionDtoSchema>
export type TransactionSummaryDto = z.infer<typeof TransactionSummaryDtoSchema>
export type PayoutDetailsDto = z.infer<typeof PayoutDetailsDtoSchema>
export type PayoutItemDto = z.infer<typeof PayoutItemDtoSchema>
